import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { useDropzone } from 'react-dropzone';
import { FaxDraftApi, FaxDraftQueries } from '@frontend/api-fax-draft';
import { Page } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { SpinningLoader, useAlert } from '@frontend/design-system';
import {
  MediaThumbnail,
  MediaThumbnailPlaceholder,
  SortableMediaThumbnail,
} from '../components/document-uploader/media-thumbnail';
import { UploadBox } from '../components/document-uploader/upload-box';
import { LeaveConfirmationModal } from '../components/draft/LeaveConfirmationModal';
import { EmptyStateGraphic } from '../components/empty-state';
import { FaxCreationFooter } from '../components/fax-creation-footer/fax-creation-footer';
import { FaxCreationStepper } from '../components/stepper/fax-creation-stepper';

const MAX_FILES_AT_ONCE = 5;
const MAX_FILE_SIZE = 50;

//for if we want to animate presence in the thumbnails.
//I set this up, but it was a little slow/choppy, so I took it out
// const _defaultThumbnailAnimation = {
//   initial: { opacity: 0 },
//   animate: { opacity: 1 },
//   exit: { opacity: 0 },
// };

export const FaxDraftUploadPage = ({ draftId }: { draftId: string }) => {
  const { selectedLocationId } = useSearch({ from: '/fax/drafts/$draftId/upload', shouldThrow: false }) ?? {};
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationId || selectedLocationIds[0];
  const navigate = useNavigate();
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const [activeId, setActiveId] = useState<string>();
  const [filesBeingUploaded, setFilesBeingUploaded] = useState<File[]>([]);

  //we need this to hold the sorted files while we wait for the server to update and refetch, otherwise it feels slow and clunky
  const [tempUploadedFiles, setTempUploadedFiles] = useState<NonNullable<typeof draft>['uploadedFiles']>();

  const {
    data: draft,
    refetch,
    isLoading: isLoadingDraft,
  } = FaxDraftQueries.useGetDraft(locationId, draftId, {
    enabled: !!draftId,
    keepPreviousData: true,
    refetchOnMount: true,
    retry: 2,
  });

  const { mutateAsync: setUploadedFiles, isLoading: isSettingUploadedFiles } = FaxDraftQueries.useSetUploadedFiles({});
  const { mutateAsync: removeUploadedFile, isLoading: isRemoveUploadedFileLoading } =
    FaxDraftQueries.useUnsetUploadedFile({});
  const { mutateAsync: _mergeFinal } = FaxDraftQueries.useMergeFinal({});
  const { mutateAsync: renderCoversheet, isLoading: isRerendering } = FaxDraftQueries.useRenderCoversheet({});
  const { mutateAsync: deleteUploadedCoversheet, isLoading: isDeletingUploadedCoversheet } =
    FaxDraftQueries.useUnsetCoversheetUploadedFile({});
  const { mutateAsync: deleteHtmlCoversheet, isLoading: isDeletingHtmlCoversheet } =
    FaxDraftQueries.useUnsetCoversheetHtml({});
  const {
    mutateAsync: mergeUploadedFiles,
    isLoading: isMerging,
    isSuccess: _isMerged,
  } = FaxDraftQueries.useMergeUploadedFiles({});

  const mb = 1000000;
  const totalSize = (draft?.uploadedFiles?.reduce((acc, file) => acc + parseInt(file.sizeBytes as any), 0) ?? 0) / mb;
  const isUploading = filesBeingUploaded?.length > 0;
  const isDisabled = isUploading || isSettingUploadedFiles;

  const isDraftEmpty =
    !draft?.uploadedCoversheet?.mediaId && !draft?.coversheet?.html && !draft?.uploadedFiles?.[0]?.mediaId;

  const isContinueDisabled =
    isUploading ||
    isSettingUploadedFiles ||
    isMerging ||
    isDeletingHtmlCoversheet ||
    isDeletingUploadedCoversheet ||
    isDraftEmpty;

  const updateFiles = (mediaIds: string[]) => {
    if (!draft?.id) {
      return;
    }
    setTempUploadedFiles(
      mediaIds
        .map((id) => draft.uploadedFiles?.find((file) => file.mediaId === id))
        .filter((file): file is NonNullable<typeof file> => !!file)
    );
    return setUploadedFiles({
      locationId,
      draftId: draft.id,
      mediaIds,
    }).then(() => {
      //note: these can happen simultaneously, so no need to await here
      mergeUploadedFiles({ draftId: draft.id, locationId: draft.locationId });
      refetch().then(() => {
        setTempUploadedFiles(undefined);
        setFilesBeingUploaded([]);
      });
    });
  };

  const {
    getRootProps: getRootDropzoneProps,
    // getInputProps: getDropzoneInputProps,
    isDragActive: isDropzoneDragActive,
    open: openFileUploader,
  } = useDropzone({
    accept: {
      'image/gif': ['.gif'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
    },
    //using noClick/noKeyboard here because we'll programmatically trigger the file dialog when they click the button (rather than whole dropzone area)
    noClick: true,
    noKeyboard: true,
    maxFiles: MAX_FILES_AT_ONCE,
    maxSize: MAX_FILE_SIZE * mb,
    onDropRejected(fileRejections) {
      if (fileRejections.find((rejection) => rejection.errors.find((error) => error.code === 'too-many-files'))) {
        return alert.error(t(`Only {{amount}} files can be uploaded at once`, { amount: MAX_FILES_AT_ONCE }));
      }
      if (fileRejections.find((rejection) => rejection.errors.find((error) => error.code === 'file-invalid-type'))) {
        return alert.error(t('Invalid file type'));
      }
      if (fileRejections.find((rejection) => rejection.errors.find((error) => error.code === 'file-too-large'))) {
        return alert.error(t('File too large. Max file size is {{amount}}', { amount: MAX_FILE_SIZE + 'MB' }));
      }
      return;
    },
    onDrop: (files) => {
      const newFilesSizeMB = files.reduce((acc, file) => acc + file.size, 0) / mb;
      if (totalSize + newFilesSizeMB >= MAX_FILE_SIZE) {
        return alert.error(t('Total upload size cannot exceed {{amount}}MB', { amount: MAX_FILE_SIZE }));
      }
      if (!draft) {
        return alert.error(t('Draft not found'));
      }

      return Promise.allSettled(
        files.map((file) => {
          setFilesBeingUploaded((prev) => [...prev, file]);
          return FaxDraftApi.UploadFaxMedia([file], locationId).finally(() => {
            // setFilesBeingUploaded((prev) => prev.filter((f) => f !== file));
          });
        })
      )
        .then((responses) => {
          const uploads = responses.reduce((acc, res) => {
            if (res.status === 'fulfilled') {
              return [...acc, ...res.value];
            } else {
              return acc;
            }
          }, [] as Awaited<ReturnType<typeof FaxDraftApi.UploadFaxMedia>>);
          updateFiles([
            ...(draft.uploadedFiles?.map((file) => file.mediaId) ?? []),
            ...uploads.filter((upload) => !upload.errorMessage).map((media) => media.mediaId),
          ]);

          const failures = responses
            .filter((res): res is PromiseRejectedResult => res.status === 'rejected')
            .map((res) => res.reason);
          if (failures.length) {
            alert.error(`
              ${t(`Error uploading {{count}} files`, { count: failures.length })}\n
              ${failures.map((failure) => failure.message).join('\n\n')}
            `);
          }
        })
        .catch((_err) => {
          alert.error(t('Error uploading files'));
        });
    },
  });

  type Media = { id: string; page: number; endPage?: number };
  const media = useMemo(() => {
    //respect tempUploadedFiles over draft.uploadedFiles
    return (tempUploadedFiles ?? draft?.uploadedFiles)?.reduce((acc, file, i) => {
      const prev = i > 0 ? acc[i - 1] : undefined;
      const start = prev ? (prev.endPage ? prev.endPage + 1 : prev.page + 1) : 1;
      const end = start + Math.max(file.pages, 1) - 1;
      return [...acc, { id: file.smallThumbnailId, page: start, endPage: end, pages: file.pages }];
    }, [] as Media[]);
  }, [draft?.uploadedFiles, tempUploadedFiles]);

  //The coversheet should be getting re-rendered on the previous page, but just to be safe,
  //when the user lands here we will re-render it to make sure it's up to date
  useEffect(() => {
    if (draft?.coversheet?.html && !draft?.coversheet?.smallThumbnailId) {
      renderCoversheet({ locationId, draftId }).finally(() => refetch());
    }
  }, [draft?.coversheet?.html]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const deleteCoversheet = () => {
    //note: we want both of these if statements to execute if the conditions are met. Don't return inside them
    if (draft?.coversheet?.html) {
      deleteHtmlCoversheet({ draftId, locationId }).then(() => {
        refetch();
      });
    }
    if (draft?.uploadedCoversheet?.smallThumbnailId) {
      deleteUploadedCoversheet({ draftId, locationId }).then(() => {
        refetch();
      });
    }
  };

  const saveAndContinue = () => {
    navigate({ to: '/fax/drafts/$draftId/finalize', params: { draftId }, search: { selectedLocationId } });
  };

  const back = () => {
    navigate({ to: '/fax/drafts/$draftId/cover', params: { draftId }, search: { selectedLocationId } });
  };

  // This button was removed from the UI, but it might come back
  // const previewFax = async () => {
  //   if (!draft?.mergedFile?.mediaId) {
  //     return alert.error(t('Preview not available'));
  //   }

  //   setIsLoadingPreview(true);
  //   return FaxDraftApi.getFaxMedia(draft.mergedFile.mediaId)
  //     .then((res) => {
  //       const url = URL.createObjectURL(res);
  //       window.open(url, '_blank');
  //     })
  //     .catch((_err) => {
  //       alert.error(t('Error generating preview'));
  //     })
  //     .finally(() => setIsLoadingPreview(false));
  // };

  const onStepClick = (stepIndex: number) => {
    const step = stepIndex + 1;
    if (step === 1) {
      return navigate({ to: '/fax/drafts/$draftId/begin', params: { draftId }, search: { selectedLocationId } });
    }
    if (step === 2) {
      navigate({
        to: '/fax/drafts/$draftId/cover',
        params: { draftId },
        search: { selectedLocationId },
      });
    }
    return;
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(`${event.active.id}`);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(undefined);
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!draft?.uploadedFiles?.length) {
      return;
    }
    const { active, over } = event;
    if (over?.id && active.id !== over?.id) {
      const prev = draft.uploadedFiles;
      //it's important we use smallThumbnailId here because that is the id we are passing the sortable thumbnail components
      //TODO: maybe change this, if this is too fragile?
      const oldIndex = prev.findIndex((v) => v.smallThumbnailId === active.id);
      const newIndex = prev.findIndex((v) => v.smallThumbnailId === over.id);
      const next = arrayMove(prev, oldIndex, newIndex).map((file) => file.mediaId);
      updateFiles(next);
    }
    setActiveId(undefined);
  };

  const onDeleteMedia = (id: string) => {
    if (!draft) {
      alert.error(t('Draft not found'));
      return;
    }
    const mediaId = draft.uploadedFiles?.find((file) => file.smallThumbnailId === id)?.mediaId;
    if (!mediaId) {
      alert.error(t('Media not found.'));
      return;
    }
    removeUploadedFile({ draftId, locationId, mediaId }).then((res) => {
      if (res.uploadedFiles?.length) mergeUploadedFiles({ draftId: draft.id, locationId: draft.locationId });
      refetch().then(() => {
        setTempUploadedFiles(undefined);
        setFilesBeingUploaded([]);
      });
    });
  };

  const coversheetStackSize = draft?.uploadedCoversheet?.largeThumbnailIds?.length ?? 0;

  if (!draft) {
    return (
      <Page
        showBackBtn
        title={t('Draft Not Found')}
        customBackFn={() => {
          navigate({ to: '/fax/$filter', params: { filter: 'drafts' } });
        }}
        loading={isLoadingDraft}
      >
        <EmptyStateGraphic />
      </Page>
    );
  }

  const activeMedia = activeId ? media?.find((v) => v.id === activeId) : undefined;
  return (
    <section
      css={css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <header
        css={css`
          border-bottom: 1px solid ${theme.colors.neutral20};
          padding: ${isMobile ? theme.spacing(2) : theme.spacing(3)} 0;
        `}
      >
        <FaxCreationStepper
          currentStep={3}
          onStepClick={onStepClick}
          onContinue={saveAndContinue}
          continueDisabled={isContinueDisabled}
          continueLoading={
            isUploading ||
            isMerging ||
            isSettingUploadedFiles ||
            isDeletingHtmlCoversheet ||
            isDeletingUploadedCoversheet
          }
        />
      </header>
      <div
        {...getRootDropzoneProps()}
        css={css`
          padding: ${theme.spacing(3)};
          height: 100%;
          overflow: scroll;
          background: ${theme.colors.neutral5};
        `}
      >
        <ul
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: ${theme.spacing(2)};
            list-style-type: none;
            margin: 0;
            padding: 0;
            border-radius: 8px;
            ${isDropzoneDragActive
              ? css`
                  background: ${theme.colors.neutral20};
                  border: 3px dashed ${theme.colors.neutral30};
                  transition: opacity 0.3s;
                  li {
                    opacity: 0;
                  }
                `
              : ''}
            li {
              transition: opacity 0.3s;
              opacity: ${isDisabled ? 0.5 : 1};
            }
          `}
        >
          {isRerendering ? (
            <MediaThumbnailPlaceholder title={t('Cover Sheet')} key={'coversheet'}>
              <SpinningLoader size='small' />
            </MediaThumbnailPlaceholder>
          ) : draft?.coversheet?.smallThumbnailId ? (
            <MediaThumbnail
              page='cover'
              id={draft?.coversheet?.smallThumbnailId}
              onDeleteClick={deleteCoversheet}
              handleBack={back}
              locationId={locationId}
            />
          ) : draft?.uploadedCoversheet?.smallThumbnailId ? (
            <MediaThumbnail
              page={coversheetStackSize > 1 ? 1 : 'cover'}
              endPage={coversheetStackSize > 1 ? coversheetStackSize : 0}
              id={draft?.uploadedCoversheet?.smallThumbnailId}
              onDeleteClick={isDisabled ? undefined : deleteCoversheet}
              handleBack={back}
              locationId={locationId}
              stackCoversheet={coversheetStackSize > 1}
            />
          ) : null}
          <DndContext
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            // modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
            sensors={sensors}
            collisionDetection={closestCenter}
          >
            <SortableContext items={media ?? []} strategy={rectSortingStrategy}>
              {media?.map((file) => {
                return (
                  <SortableMediaThumbnail
                    style={activeMedia === file ? { opacity: 0 } : undefined}
                    key={file.id}
                    id={file.id}
                    page={file.page}
                    endPage={file.endPage}
                    onDeleteClick={isDisabled ? undefined : () => onDeleteMedia(file.id)}
                    locationId={locationId}
                    isDeleteLoading={isRemoveUploadedFileLoading}
                    // {...defaultThumbnailAnimation}
                  />
                );
              })}

              {filesBeingUploaded.map((file) => (
                <MediaThumbnailPlaceholder title={t('Uploading')} key={file.name}>
                  <SpinningLoader size='small' />
                </MediaThumbnailPlaceholder>
              ))}

              {!isUploading && <UploadBox onClick={() => openFileUploader()} />}
            </SortableContext>
            <DragOverlay adjustScale style={{ transformOrigin: '0 0 ' }}>
              {activeMedia ? (
                <MediaThumbnail
                  key={activeMedia.id}
                  id={activeMedia.id}
                  page={activeMedia.page}
                  endPage={activeMedia.endPage}
                  isDragging={true}
                  locationId={locationId}
                  // {...defaultThumbnailAnimation}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </ul>
      </div>
      <FaxCreationFooter totalSize={isMerging || isUploading ? 'loading' : totalSize} />
      <LeaveConfirmationModal draftId={draftId} shouldBlock={true} locationId={locationId} />
    </section>
  );
};
